import template from './estate-map-view.pug';
import { debounce as _debounce } from 'lodash';

export const EstateMapViewComponent = {
  template,
  bindings: {
    estates: '<',
    onMarkerClick: '&?',
  },
  controller: class EstateMapViewComponent {
    constructor(Geopify, Account, $async, $log, $state) {
      'ngInject';

      this.Geopify = Geopify;
      this.Account = Account;
      this.$log = $log;
      this.$state = $state;
      this.markers = [];
      this.mapZoomEnd = _debounce($async(this.mapZoomEnd.bind(this)), 850);
      this.initializeMap = $async(this.initializeMap.bind(this));
      this.mapInitialized = false;
    }

    $onInit() {
      if (this.hasValidEstates()) {
        this.initializeMap();
      }
    }

    $onChanges({ estates }) {
      if (estates && estates.currentValue) {
        this.estates = estates.currentValue;

        if (this.hasValidEstates()) {
          if (!this.mapInitialized) {
            this.initializeMap();
          } else {
            this.updateMarkers();
          }
        }
      }
    }

    hasValidEstates() {
      return this.estates &&
             this.estates.length > 0 &&
             this.estates.some(estate =>
               estate.attributes &&
               estate.attributes.latitude &&
               estate.attributes.longitude
             );
    }

    getValidEstates() {
      return this.estates.filter(estate =>
        estate.attributes &&
        estate.attributes.latitude &&
        estate.attributes.longitude
      );
    }

    async initializeMap() {
      try {
        this.map = this.Geopify.initMap('estates-map');
        this.map.on('zoomend', () => {
          this.mapZoomEnd();
        });

        this.Geopify.addControls();

        const countryData = await this.Account.getCountryData();
        const geo = countryData.data.geo;
        this.map.fitBounds([
          [geo.max_longitude, geo.max_latitude],
          [geo.min_longitude, geo.min_latitude],
        ]);
        this.map.setCenter([geo.longitude, geo.latitude]);
        this.map.setZoom(3);

        this.mapInitialized = true;
        this.updateMarkers();
      } catch (e) {
        this.$log.error(e);
      }
    }

    createCircleMarker(estate) {
      const el = document.createElement('div');
      el.className = 'circle-marker';
      el.style.width = '20px';
      el.style.height = '20px';
      el.style.borderRadius = '50%';
      el.style.backgroundColor = estate.color;
      el.style.border = '2px solid white';
      el.style.cursor = 'pointer';

      const marker = new this.Geopify.Marker({
        element: el,
        draggable: false
      }).setLngLat([estate.longitude, estate.latitude]);

      const popup = this.Geopify.createPopup({
        offset: [0, -20], // Adjusted offset to move popup up
        closeButton: false,
        closeOnClick: false,
        className: 'estate-popup',
        maxWidth: '260px'
      });

      const imageHtml = estate.image_url ? `
        <div class="estate-popup-image">
          <img
            src="${estate.image_url}"
            alt="${estate.title || 'Estate'}"
            onerror="this.style.display='none'"
            width="320"
            height="180"
            onerror="this.style.display='none'"
            loading="lazy"
          />
        </div>
      ` : '';

      popup.setHTML(`
        <div class="estate-popup-content">
          ${imageHtml}
          <div class="estate-popup-info">
            <h5>${estate.title || 'Estate'}</h5>
            <p>${estate.address || ''}</p>
            <a href="javascript:void(0)" class="view-details-link">
              View Details
            </a>
          </div>
        </div>
      `);

      marker.addTo(this.map);

      // Add hover events
      el.addEventListener('mouseenter', () => {
        popup.setLngLat(marker.getLngLat())
          .addTo(this.map);

        const popupElement = popup.getElement();
        const viewDetailsLink = popupElement.querySelector('.view-details-link');
        if (viewDetailsLink) {
          viewDetailsLink.addEventListener('click', () => {
            if (this.onMarkerClick) {
              this.onMarkerClick({ $event: { estate } });
            }
          });
        }
      });

      el.addEventListener('mouseleave', () => {
        setTimeout(() => {
          const popupElement = popup.getElement();
          if (popupElement) {
            if (!popupElement.matches(':hover')) {
              popup.remove();
            } else {
              popupElement.addEventListener('mouseleave', () => {
                popup.remove();
              }, { once: true });
            }
          }
        }, 100);
      });

      return marker;
    }

    updateMarkers() {
      if (!this.mapInitialized) return;

      this.markers.forEach(marker => marker.remove());
      this.markers = [];

      const validEstates = this.getValidEstates();

      if (!validEstates.length) return;

      validEstates.forEach(estate => {
        const marker = this.createCircleMarker(estate.attributes);
        if (marker) {
          this.markers.push(marker);
        }
      });

      if (this.markers.length > 0) {
        const bounds = new this.Geopify.LngLatBounds();
        this.markers.forEach(marker => {
          bounds.extend(marker.getLngLat());
        });
        this.map.fitBounds(bounds, {
          padding: 50,
          maxZoom: 15
        });
      }
    }

    async mapZoomEnd() {
      const zoom = this.map.getZoom();
    }
  }
};
